/**
 * ReconciliationBanner — Phase 81.3
 * ---------------------------------
 * Compact tray at the top of the Transactions page that surfaces
 * "possible matches" between the user's manual entries and later-imported
 * bank statement rows.
 *
 * Backed by:
 *   • GET  /api/reconciliation/pending  — hydrated candidate list
 *   • POST /api/reconciliation/confirm  — merge a candidate
 *   • POST /api/reconciliation/reject   — pair-scoped reject
 *
 * Design contract:
 *   • Zero visible chrome when count === 0. No banner, no loading spinner.
 *   • Fully cold-start-safe: swallows 404 / "table missing" errors from
 *     the backend and silently no-ops (matches the backend's own
 *     philosophy — reconciliation is an *addition* to the app, never a
 *     regression path).
 *   • Never blocks initial page render — banner mounts lazily and does
 *     its own fetch in a useEffect.
 *   • The review sheet renders side-by-side (Manual | Statement) with a
 *     small score chip + reasons breakdown so users can decide with
 *     confidence.
 *   • Post-action: refresh the pending list AND fire an onResolved()
 *     callback so the parent Transactions page can reload its ledger
 *     (auto-merges will have removed the manual row).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Sparkles, Check, X, Info } from 'lucide-react';
import { formatMoney } from '@/lib/format';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || import.meta.env?.REACT_APP_BACKEND_URL;

// -----------------------------------------------------------------------------
// helpers
// -----------------------------------------------------------------------------

/** Human-friendly label for a candidate reference kind badge. */
const REF_KIND_LABEL = {
  amount_match: 'amount',
  account_match: 'account',
  date_delta_days: 'date',
  merchant_tokens: 'merchant',
  hard_gates_pass: 'gates',
};

/** Cold-start-safe: treat "table missing" or 404 as "no candidates". */
function _isNoData(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const msg = typeof body === 'string' ? body : JSON.stringify(body || '');
  return (
    status === 404 ||
    /reconciliation_candidates.*does not exist/i.test(msg) ||
    /PGRST205/i.test(msg) ||
    /migration pending/i.test(msg)
  );
}

/** Small pill showing a single scoring signal — used inside the review sheet. */
function SignalPill({ label, value, positive }) {
  const cls = positive
    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
    : 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300';
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[10.5px] tabular-nums ${cls}`}>
      {label}
      {value !== undefined && value !== null && (
        <span className="ml-1 font-medium">{value}</span>
      )}
    </span>
  );
}

// -----------------------------------------------------------------------------
// TxnPreview — one side of the side-by-side card
// -----------------------------------------------------------------------------
function TxnPreview({ label, txn, accent }) {
  const isOutflow = Number(txn?.amount || 0) < 0;
  const displayAmount = Math.abs(Number(txn?.amount || 0));
  return (
    <div
      className={`rounded-xl border p-3 space-y-1.5 ${
        accent === 'primary'
          ? 'border-primary/30 bg-primary/[0.03]'
          : 'border-border/60 bg-muted/20'
      }`}
      data-testid={`recon-preview-${label.toLowerCase()}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-medium">
          {label}
        </div>
        <div className={`text-[11px] tabular-nums font-medium ${isOutflow ? 'text-foreground' : 'text-emerald-700 dark:text-emerald-400'}`}>
          {isOutflow ? '−' : '+'}
          {formatMoney(displayAmount, undefined, { compact: false })}
        </div>
      </div>
      <div className="text-[13px] font-medium leading-snug break-words">
        {txn?.description || <span className="text-muted-foreground italic">No description</span>}
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground tabular-nums">
        <span>{txn?.txn_date || '—'}</span>
        {txn?.category && (
          <>
            <span className="opacity-40">·</span>
            <span>{txn.category}</span>
          </>
        )}
        {txn?.payment_method && (
          <>
            <span className="opacity-40">·</span>
            <span className="capitalize">{String(txn.payment_method).replace('_', ' ')}</span>
          </>
        )}
        {txn?.category_source === 'user_confirmed' && (
          <>
            <span className="opacity-40">·</span>
            <span className="text-primary/80">user-confirmed</span>
          </>
        )}
      </div>
      {txn?.note && (
        <div className="text-[11px] text-muted-foreground italic border-l-2 border-border/60 pl-2 mt-1">
          {txn.note}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------
export default function ReconciliationBanner({ session, onResolved }) {
  const [candidates, setCandidates] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);

  const authHeaders = useMemo(() => {
    if (!session?.access_token) return null;
    return { Authorization: `Bearer ${session.access_token}` };
  }, [session?.access_token]);

  const fetchPending = useCallback(async () => {
    if (!authHeaders || !BACKEND_URL) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/reconciliation/pending`,
        { headers: authHeaders, timeout: 12000 },
      );
      setCandidates(data?.candidates || []);
      setCount(data?.count || 0);
    } catch (err) {
      if (_isNoData(err)) {
        setCandidates([]);
        setCount(0);
      } else {
        // Only log — never surface to the user. Reconciliation is a bonus
        // feature; a fetch failure must never make the Transactions page
        // look broken.
        console.warn('reconciliation/pending failed:', err?.response?.status, err?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  const handleConfirm = async (candidateId) => {
    if (!authHeaders || !BACKEND_URL || resolvingId) return;
    setResolvingId(candidateId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/reconciliation/confirm`,
        { candidate_id: candidateId },
        { headers: authHeaders, timeout: 15000 },
      );
      toast.success('Matched. Your notes and categories were preserved.');
      // Optimistic: remove locally + refetch to sync sibling supersede.
      setCandidates((prev) => prev.filter((c) => c.id !== candidateId));
      setCount((n) => Math.max(0, n - 1));
      onResolved?.();
      // Full refetch in background — sibling candidates may have been
      // superseded by the merge; keep the tray consistent.
      fetchPending();
    } catch (err) {
      console.error('reconciliation/confirm failed:', err?.response?.data || err?.message);
      toast.error(err?.response?.data?.detail || 'Could not merge. Please try again.');
    } finally {
      setResolvingId(null);
    }
  };

  const handleReject = async (candidateId) => {
    if (!authHeaders || !BACKEND_URL || resolvingId) return;
    setResolvingId(candidateId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/reconciliation/reject`,
        { candidate_id: candidateId },
        { headers: authHeaders, timeout: 15000 },
      );
      toast.info('Not a match — we won’t suggest this pair again.');
      setCandidates((prev) => prev.filter((c) => c.id !== candidateId));
      setCount((n) => Math.max(0, n - 1));
    } catch (err) {
      console.error('reconciliation/reject failed:', err?.response?.data || err?.message);
      toast.error(err?.response?.data?.detail || 'Could not reject. Please try again.');
    } finally {
      setResolvingId(null);
    }
  };

  // ------- Zero-state: render nothing at all (design guideline) --------------
  if (!authHeaders) return null;
  if (count === 0 && !loading) return null;

  return (
    <>
      <div
        className="mb-4 rounded-2xl border border-primary/25 bg-gradient-to-r from-primary/[0.06] via-primary/[0.03] to-transparent px-4 py-3 flex items-center gap-3"
        data-testid="reconciliation-banner"
      >
        <div className="rounded-full bg-primary/12 p-2 shrink-0">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-foreground">
            {count === 1
              ? 'We found 1 possible match between your manual entries and imported statements.'
              : `We found ${count} possible matches between your manual entries and imported statements.`}
          </div>
          <div className="text-[11.5px] text-muted-foreground">
            Review each pair; we&rsquo;ll preserve your notes and categories on merge.
          </div>
        </div>
        <Button
          type="button"
          variant="default"
          size="sm"
          className="rounded-xl shrink-0"
          onClick={() => setSheetOpen(true)}
          data-testid="reconciliation-banner-open-review"
        >
          Review
        </Button>
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl p-0 flex flex-col"
          data-testid="reconciliation-sheet"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-border/60 shrink-0">
            <SheetTitle className="text-base font-semibold">
              Reconcile manual entries with statements
            </SheetTitle>
            <SheetDescription className="text-[12px]">
              {count > 0
                ? `${count} pair${count === 1 ? '' : 's'} to review. Statement rows become
                   canonical on merge, but your category, notes, and payment method
                   from the manual entry always survive.`
                : 'All caught up — no pending matches.'}
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1 px-5 py-4">
            {loading && candidates.length === 0 && (
              <div className="flex items-center gap-2 text-[12px] text-muted-foreground py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading candidates…
              </div>
            )}

            {!loading && candidates.length === 0 && (
              <div className="flex flex-col items-center gap-2 py-10 text-center" data-testid="reconciliation-sheet-empty">
                <div className="rounded-full bg-emerald-500/10 p-3">
                  <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="text-sm font-medium">All caught up</div>
                <div className="text-[12px] text-muted-foreground max-w-xs">
                  There are no pending matches right now. We&rsquo;ll surface new ones automatically after your next statement import.
                </div>
              </div>
            )}

            <div className="space-y-4">
              {candidates.map((c) => {
                const reasons = c.match_reasons || {};
                const breakdown = c.score_breakdown || {};
                const stmt = c.statement_txn;
                const man = c.manual_txn;
                const dateDelta = reasons.date_delta_days;
                const sharedTokens = reasons.merchant_tokens || [];
                const hardGates = reasons.hard_gates_pass;
                const resolvingThis = resolvingId === c.id;

                return (
                  <div
                    key={c.id}
                    className="rounded-2xl border border-border/60 bg-card p-3 space-y-3"
                    data-testid={`reconciliation-candidate-${c.id.slice(0, 8)}`}
                  >
                    {/* Score chip + signals row */}
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant={c.score >= 90 ? 'default' : 'secondary'}
                        className="rounded-full text-[10.5px] tabular-nums"
                      >
                        Match score {c.score}/100
                      </Badge>
                      <SignalPill
                        label="amount"
                        value={breakdown.amount === 40 ? 'exact' : `${breakdown.amount}/40`}
                        positive={breakdown.amount === 40}
                      />
                      <SignalPill
                        label="account"
                        value={breakdown.account === 25 ? 'same' : 'diff'}
                        positive={breakdown.account === 25}
                      />
                      <SignalPill
                        label="date"
                        value={dateDelta !== null && dateDelta !== undefined ? `Δ${dateDelta}d` : '—'}
                        positive={breakdown.date >= 12}
                      />
                      <SignalPill
                        label="merchant"
                        value={sharedTokens.length ? sharedTokens.slice(0, 2).join(', ') : 'none'}
                        positive={sharedTokens.length > 0}
                      />
                      {hardGates === false && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10.5px] text-amber-700 dark:text-amber-300"
                          title="One or more auto-merge safety gates did not pass; kept for your review."
                        >
                          <Info className="h-3 w-3" /> review
                        </span>
                      )}
                    </div>

                    {/* Side-by-side previews */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <TxnPreview label="Your manual entry" txn={man} accent="primary" />
                      <TxnPreview label="Statement row" txn={stmt} accent="muted" />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="rounded-xl"
                        onClick={() => handleReject(c.id)}
                        disabled={resolvingThis}
                        data-testid={`reconciliation-candidate-${c.id.slice(0, 8)}-reject`}
                      >
                        {resolvingThis ? (
                          <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Working…</>
                        ) : (
                          <><X className="h-3.5 w-3.5 mr-1" /> Not a match</>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        className="rounded-xl"
                        onClick={() => handleConfirm(c.id)}
                        disabled={resolvingThis}
                        data-testid={`reconciliation-candidate-${c.id.slice(0, 8)}-confirm`}
                      >
                        {resolvingThis ? (
                          <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Merging…</>
                        ) : (
                          <><Check className="h-3.5 w-3.5 mr-1" /> Merge</>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="px-5 py-3 border-t border-border/60 shrink-0 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>
              High-confidence matches were merged automatically on your last import.
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-xl h-7 px-2 text-[11px]"
              onClick={fetchPending}
              disabled={loading}
              data-testid="reconciliation-sheet-refresh"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
